import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { T, useTranslate } from '@tolgee/react';
import DistributorBanner from '../../../components/DistributorBanner';
import apiManager from '../../../lib/api';
import { useZarazTracking } from '../../../lib/zaraz/useZarazTracking';

// reCAPTCHA v3 site key
const RECAPTCHA_SITE_KEY = '6Lcg31gsAAAAALZgvPJaTe9t8_KNHOz2WV9gC32i';

// Declare grecaptcha type
declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

const RegisterPage = () => {
  const { auth, clearError } = useAuth();
  const navigate = useNavigate();
  const { trackSignedUp } = useZarazTracking();
  const { t } = useTranslate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Load reCAPTCHA v3 script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    document.head.appendChild(script);

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector(`script[src*="recaptcha"]`);
      if (existingScript) {
        existingScript.remove();
      }
      // Remove reCAPTCHA badge
      const badge = document.querySelector('.grecaptcha-badge');
      if (badge) {
        badge.remove();
      }
    };
  }, []);

  // Execute reCAPTCHA and get token
  const executeRecaptcha = useCallback(async (): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!window.grecaptcha) {
        reject(new Error('reCAPTCHA not loaded'));
        return;
      }
      window.grecaptcha.ready(async () => {
        try {
          const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'register' });
          resolve(token);
        } catch (error) {
          reject(error);
        }
      });
    });
  }, []);
  
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  
  const [formData, setFormData] = useState({
    // Datos fiscales
    nombreFiscal: '',
    nombreComercial: '',
    iae: '',
    nifCif: '',
    
    // Dirección
    calle: '',
    ciudad: '',
    provincia: '',
    codigoPostal: '',
    pais: 'España',
    
    // Contacto
    telefono: '',
    fax: '',
    movil: '',
    personaContacto: '',
    email: '',
    web: '',
    
    // Comentarios
    comentario: '',
    
    // Acceso
    password: '',
    confirmPassword: '',
  });
  
  const [formErrors, setFormErrors] = useState({
    nombreFiscal: '',
    nombreComercial: '',
    iae: '',
    nifCif: '',
    calle: '',
    ciudad: '',
    provincia: '',
    codigoPostal: '',
    telefono: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  
  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const validateCurrentStep = () => {
    let isValid = true;
    const errors = { ...formErrors };

    switch (currentStep) {
      case 1: // Datos fiscales
        if (!formData.nombreFiscal.trim()) {
          errors.nombreFiscal = 'El nombre fiscal es obligatorio';
          isValid = false;
        }
        if (!formData.nombreComercial.trim()) {
          errors.nombreComercial = 'El nombre comercial es obligatorio';
          isValid = false;
        }
        if (!formData.iae.trim()) {
          errors.iae = 'El IAE es obligatorio';
          isValid = false;
        }
        if (!formData.nifCif.trim()) {
          errors.nifCif = 'El NIF/CIF es obligatorio';
          isValid = false;
        }
        break;

      case 2: // Dirección
        if (!formData.calle.trim()) {
          errors.calle = 'La calle es obligatoria';
          isValid = false;
        }
        if (!formData.ciudad.trim()) {
          errors.ciudad = 'La ciudad es obligatoria';
          isValid = false;
        }
        if (!formData.provincia.trim()) {
          errors.provincia = 'La provincia es obligatoria';
          isValid = false;
        }
        if (!formData.codigoPostal.trim()) {
          errors.codigoPostal = 'El código postal es obligatorio';
      isValid = false;
    }
        break;

      case 3: // Contacto
        if (!formData.telefono.trim()) {
          errors.telefono = 'El teléfono es obligatorio';
          isValid = false;
        }
    if (!formData.email.trim()) {
      errors.email = 'El email es obligatorio';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'El email no es válido';
      isValid = false;
    }
        break;
    
      case 4: // Acceso
    if (!formData.password.trim()) {
      errors.password = 'La contraseña es obligatoria';
      isValid = false;
    } else if (formData.password.length < 6) {
      errors.password = 'La contraseña debe tener al menos 6 caracteres';
      isValid = false;
    }
    if (!formData.confirmPassword.trim()) {
      errors.confirmPassword = 'Confirma tu contraseña';
      isValid = false;
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Las contraseñas no coinciden';
      isValid = false;
        }
        break;
    }
    
    setFormErrors(errors);
    return isValid;
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Limpiar error del campo
    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors({
        ...formErrors,
        [name]: '',
      });
    }
    
    // Limpiar error general
    if (auth.error) {
      clearError();
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateCurrentStep()) {
      return;
    }

    if (currentStep < totalSteps) {
      nextStep();
    } else {
      // Enviar solicitud final
      setIsSubmitting(true);
      setSubmitError(null);

      try {
        // Obtener token de reCAPTCHA
        const recaptchaToken = await executeRecaptcha();

        // Preparar datos para el registro
        const registerData = {
          name: formData.nombreComercial || formData.nombreFiscal,
          email: formData.email,
          password: formData.password,
          nif: formData.nifCif,
          phone: formData.telefono,
          address: {
            street: formData.calle,
            city: formData.ciudad,
            postalCode: formData.codigoPostal,
            country: formData.pais,
          },
          recaptchaToken,
          // Campos adicionales
          nombreFiscal: formData.nombreFiscal,
          nombreComercial: formData.nombreComercial,
          iae: formData.iae,
          calle: formData.calle,
          ciudad: formData.ciudad,
          provincia: formData.provincia,
          codigoPostal: formData.codigoPostal,
          pais: formData.pais,
          fax: formData.fax,
          movil: formData.movil,
          personaContacto: formData.personaContacto,
          web: formData.web,
          comentario: formData.comentario,
        };

        // Llamar al API de registro
        const response = await apiManager.auth.register(registerData);

        if (response.success) {
          // Track registration event
          trackSignedUp({
            email: formData.email,
            name: formData.nombreComercial || formData.nombreFiscal,
          });
          // Redireccionar después del envío exitoso
          navigate('/login?message=solicitud-enviada');
        } else {
          // Handle error - could be string or object with message
          const errorMessage = typeof response.error === 'string'
            ? response.error
            : response.error?.message || 'Error al enviar la solicitud';
          setSubmitError(errorMessage);
        }
      } catch (error) {
        console.error('Error en el formulario de registro:', error);
        setSubmitError('Error al procesar la solicitud. Inténtalo de nuevo.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };
  
  return (
    <>
      <Helmet>
        <title>Solicitar Acceso | Jarad - Distribuidores Mayoristas</title>
        <meta name="description" content="Solicita tu alta como distribuidor mayorista de Jarad Pet Food para acceder a precios especiales y productos premium." />
      </Helmet>

      <div className="min-h-screen" style={{backgroundColor: '#f2ede4'}}>
        <DistributorBanner />
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
          {/* Formulario - Lado izquierdo */}
          <motion.div 
            className="flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="max-w-md w-full space-y-8">
              <div className="text-center">
                <h1 className="text-4xl font-extrabold mb-2" style={{color: '#0d0d0d'}}>
                  <T keyName="auth.register.title">Solicitar Acceso</T>
                </h1>
                <p className="text-lg text-gray-600 mb-8">
                  <T keyName="auth.register.subtitle">Solicita tu alta como distribuidor mayorista</T>
                </p>
              </div>
              
              {/* Indicador de progreso */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  {[1, 2, 3, 4].map((step) => (
                    <div key={step} className="flex items-center">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors duration-200 `}
                        style={{
                          backgroundColor:
                            step === currentStep
                              ? '#f6cf02'
                              : '#0d0d0d',
                          color:
                            step === currentStep
                              ? '#0d0d0d'
                              : '#fff',
                          border:
                            step === currentStep
                              ? '2px solid #0d0d0d'
                              : '2px solid #0d0d0d',
                        }}
                      >
                        {step}
                      </div>
                      {step < 4 && (
                        <div
                          className="mx-2 transition-colors duration-200"
                          style={{
                            width: 92, // más largo
                            height: 4, // más grueso
                            borderRadius: 8,
                            backgroundColor:
                              currentStep > step
                                ? '#f6cf02'
                                : '#0d0d0d',
                          }}
                        ></div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="text-center">
                  <p className="text-sm" style={{color: '#0d0d0d'}}>
                    Paso {currentStep} de {totalSteps}
                  </p>
                </div>
              </div>
              
              <form className="space-y-6" onSubmit={handleSubmit} data-testid="register-form">
                {/* Paso 1: Datos Fiscales */}
                {currentStep === 1 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-4"
                    data-testid="step-1-fiscal-data"
                  >
                    <h3 className="text-xl font-bold mb-4" style={{color: '#0d0d0d'}}>
                      <T keyName="auth.register.fiscal_data">Datos Fiscales</T>
                    </h3>

                    <div>
                      <label htmlFor="nombreFiscal" className="block text-sm font-medium mb-2" style={{color: '#0d0d0d'}}>
                        <T keyName="auth.register.fiscal_name">Nombre fiscal *</T>
                      </label>
                      <input
                        id="nombreFiscal"
                        name="nombreFiscal"
                        type="text"
                        value={formData.nombreFiscal}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border-2 rounded-2xl focus:outline-none transition-colors ${
                          formErrors.nombreFiscal
                            ? 'border-red-300 focus:border-red-500'
                            : 'border-gray-200 focus:border-gray-400'
                        }`}
                        style={{color: '#0d0d0d'}}
                        placeholder="Nombre fiscal de la empresa"
                        data-testid="fiscal-name-input"
                      />
                      {formErrors.nombreFiscal && (
                        <p className="mt-2 text-sm text-red-600" data-testid="fiscal-name-error">{formErrors.nombreFiscal}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="nombreComercial" className="block text-sm font-medium mb-2" style={{color: '#0d0d0d'}}>
                        <T keyName="auth.register.commercial_name">Nombre comercial *</T>
                      </label>
                      <input
                        id="nombreComercial"
                        name="nombreComercial"
                        type="text"
                        value={formData.nombreComercial}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border-2 rounded-2xl focus:outline-none transition-colors ${
                          formErrors.nombreComercial
                            ? 'border-red-300 focus:border-red-500'
                            : 'border-gray-200 focus:border-gray-400'
                        }`}
                        style={{color: '#0d0d0d'}}
                        placeholder="Nombre comercial"
                        data-testid="commercial-name-input"
                      />
                      {formErrors.nombreComercial && (
                        <p className="mt-2 text-sm text-red-600" data-testid="commercial-name-error">{formErrors.nombreComercial}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="iae" className="block text-sm font-medium mb-2" style={{color: '#0d0d0d'}}>
                          <T keyName="auth.register.iae">IAE *</T>
                        </label>
                        <input
                          id="iae"
                          name="iae"
                          type="text"
                          value={formData.iae}
                          onChange={handleChange}
                          className={`w-full px-4 py-3 border-2 rounded-2xl focus:outline-none transition-colors ${
                            formErrors.iae
                              ? 'border-red-300 focus:border-red-500'
                              : 'border-gray-200 focus:border-gray-400'
                          }`}
                          style={{color: '#0d0d0d'}}
                          placeholder="Código IAE"
                          data-testid="iae-input"
                        />
                        {formErrors.iae && (
                          <p className="mt-2 text-sm text-red-600" data-testid="iae-error">{formErrors.iae}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="nifCif" className="block text-sm font-medium mb-2" style={{color: '#0d0d0d'}}>
                          <T keyName="auth.register.nif_cif">NIF/CIF *</T>
                        </label>
                        <input
                          id="nifCif"
                          name="nifCif"
                          type="text"
                          value={formData.nifCif}
                          onChange={handleChange}
                          className={`w-full px-4 py-3 border-2 rounded-2xl focus:outline-none transition-colors ${
                            formErrors.nifCif
                              ? 'border-red-300 focus:border-red-500'
                              : 'border-gray-200 focus:border-gray-400'
                          }`}
                          style={{color: '#0d0d0d'}}
                          placeholder="NIF/CIF"
                          data-testid="nif-cif-input"
                        />
                        {formErrors.nifCif && (
                          <p className="mt-2 text-sm text-red-600" data-testid="nif-cif-error">{formErrors.nifCif}</p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Paso 2: Dirección */}
                {currentStep === 2 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-4"
                    data-testid="step-2-address"
                  >
                    <h3 className="text-xl font-bold mb-4" style={{color: '#0d0d0d'}}>
                      <T keyName="auth.register.address">Dirección</T>
                    </h3>

                    <div>
                      <label htmlFor="calle" className="block text-sm font-medium mb-2" style={{color: '#0d0d0d'}}>
                        <T keyName="auth.register.street">Calle *</T>
                      </label>
                      <input
                        id="calle"
                        name="calle"
                        type="text"
                        value={formData.calle}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border-2 rounded-2xl focus:outline-none transition-colors ${
                          formErrors.calle
                            ? 'border-red-300 focus:border-red-500'
                            : 'border-gray-200 focus:border-gray-400'
                        }`}
                        style={{color: '#0d0d0d'}}
                        placeholder="Calle y número"
                        data-testid="street-input"
                      />
                      {formErrors.calle && (
                        <p className="mt-2 text-sm text-red-600" data-testid="street-error">{formErrors.calle}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                  <div>
                                                <label htmlFor="ciudad" className="block text-sm font-medium mb-2" style={{color: '#0d0d0d'}}>
                          <T keyName="auth.register.city">Ciudad *</T>
                        </label>
                    <input
                          id="ciudad"
                          name="ciudad"
                      type="text"
                          value={formData.ciudad}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border-2 rounded-2xl focus:outline-none transition-colors ${
                            formErrors.ciudad
                          ? 'border-red-300 focus:border-red-500'
                          : 'border-gray-200 focus:border-gray-400'
                      }`}
                      style={{color: '#0d0d0d'}}
                          placeholder="Ciudad"
                          data-testid="city-input"
                        />
                        {formErrors.ciudad && (
                          <p className="mt-2 text-sm text-red-600" data-testid="city-error">{formErrors.ciudad}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="provincia" className="block text-sm font-medium mb-2" style={{color: '#0d0d0d'}}>
                          <T keyName="auth.register.province">Provincia *</T>
                        </label>
                        <input
                          id="provincia"
                          name="provincia"
                          type="text"
                          value={formData.provincia}
                          onChange={handleChange}
                          className={`w-full px-4 py-3 border-2 rounded-2xl focus:outline-none transition-colors ${
                            formErrors.provincia
                              ? 'border-red-300 focus:border-red-500'
                              : 'border-gray-200 focus:border-gray-400'
                          }`}
                          style={{color: '#0d0d0d'}}
                          placeholder="Provincia"
                          data-testid="province-input"
                        />
                        {formErrors.provincia && (
                          <p className="mt-2 text-sm text-red-600" data-testid="province-error">{formErrors.provincia}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="codigoPostal" className="block text-sm font-medium mb-2" style={{color: '#0d0d0d'}}>
                        <T keyName="auth.register.postal_code">Código Postal *</T>
                      </label>
                      <input
                        id="codigoPostal"
                        name="codigoPostal"
                        type="text"
                        value={formData.codigoPostal}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border-2 rounded-2xl focus:outline-none transition-colors ${
                          formErrors.codigoPostal
                            ? 'border-red-300 focus:border-red-500'
                            : 'border-gray-200 focus:border-gray-400'
                        }`}
                        style={{color: '#0d0d0d'}}
                        placeholder="Código postal"
                        data-testid="postal-code-input"
                      />
                      {formErrors.codigoPostal && (
                        <p className="mt-2 text-sm text-red-600" data-testid="postal-code-error">{formErrors.codigoPostal}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="pais" className="block text-sm font-medium mb-2" style={{color: '#0d0d0d'}}>
                        <T keyName="auth.register.country">País *</T>
                      </label>
                      <select
                        id="pais"
                        name="pais"
                        value={formData.pais}
                        onChange={handleChange as any}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-gray-400 transition-colors"
                        style={{color: '#0d0d0d'}}
                        data-testid="country-input"
                      >
                        <optgroup label={t('countries.region.western_europe', 'Europa Occidental')}>
                          <option value="España">{t('countries.spain', 'España')}</option>
                          <option value="Portugal">{t('countries.portugal', 'Portugal')}</option>
                          <option value="Francia">{t('countries.france', 'Francia')}</option>
                          <option value="Andorra">{t('countries.andorra', 'Andorra')}</option>
                          <option value="Italia">{t('countries.italy', 'Italia')}</option>
                          <option value="Alemania">{t('countries.germany', 'Alemania')}</option>
                          <option value="Reino Unido">{t('countries.united_kingdom', 'Reino Unido')}</option>
                          <option value="Bélgica">{t('countries.belgium', 'Bélgica')}</option>
                          <option value="Países Bajos">{t('countries.netherlands', 'Países Bajos')}</option>
                          <option value="Luxemburgo">{t('countries.luxembourg', 'Luxemburgo')}</option>
                          <option value="Suiza">{t('countries.switzerland', 'Suiza')}</option>
                          <option value="Austria">{t('countries.austria', 'Austria')}</option>
                          <option value="Irlanda">{t('countries.ireland', 'Irlanda')}</option>
                          <option value="Mónaco">{t('countries.monaco', 'Mónaco')}</option>
                        </optgroup>
                        <optgroup label={t('countries.region.northern_europe', 'Europa del Norte')}>
                          <option value="Dinamarca">{t('countries.denmark', 'Dinamarca')}</option>
                          <option value="Suecia">{t('countries.sweden', 'Suecia')}</option>
                          <option value="Noruega">{t('countries.norway', 'Noruega')}</option>
                          <option value="Finlandia">{t('countries.finland', 'Finlandia')}</option>
                        </optgroup>
                        <optgroup label={t('countries.region.eastern_europe', 'Europa del Este')}>
                          <option value="Polonia">{t('countries.poland', 'Polonia')}</option>
                          <option value="República Checa">{t('countries.czech_republic', 'República Checa')}</option>
                          <option value="Hungría">{t('countries.hungary', 'Hungría')}</option>
                          <option value="Rumanía">{t('countries.romania', 'Rumanía')}</option>
                          <option value="Bulgaria">{t('countries.bulgaria', 'Bulgaria')}</option>
                          <option value="Croacia">{t('countries.croatia', 'Croacia')}</option>
                          <option value="Eslovenia">{t('countries.slovenia', 'Eslovenia')}</option>
                          <option value="Eslovaquia">{t('countries.slovakia', 'Eslovaquia')}</option>
                          <option value="Grecia">{t('countries.greece', 'Grecia')}</option>
                        </optgroup>
                        <optgroup label={t('countries.region.africa', 'África')}>
                          <option value="Marruecos">{t('countries.morocco', 'Marruecos')}</option>
                          <option value="Argelia">{t('countries.algeria', 'Argelia')}</option>
                          <option value="Túnez">{t('countries.tunisia', 'Túnez')}</option>
                          <option value="Egipto">{t('countries.egypt', 'Egipto')}</option>
                          <option value="Sudáfrica">{t('countries.south_africa', 'Sudáfrica')}</option>
                        </optgroup>
                        <optgroup label={t('countries.region.middle_east', 'Oriente Medio')}>
                          <option value="Emiratos Árabes Unidos">{t('countries.uae', 'Emiratos Árabes Unidos')}</option>
                          <option value="Arabia Saudita">{t('countries.saudi_arabia', 'Arabia Saudita')}</option>
                          <option value="Qatar">{t('countries.qatar', 'Qatar')}</option>
                          <option value="Kuwait">{t('countries.kuwait', 'Kuwait')}</option>
                          <option value="Bahréin">{t('countries.bahrain', 'Bahréin')}</option>
                          <option value="Omán">{t('countries.oman', 'Omán')}</option>
                          <option value="Israel">{t('countries.israel', 'Israel')}</option>
                          <option value="Jordania">{t('countries.jordan', 'Jordania')}</option>
                          <option value="Líbano">{t('countries.lebanon', 'Líbano')}</option>
                          <option value="Turquía">{t('countries.turkey', 'Turquía')}</option>
                        </optgroup>
                        <optgroup label={t('countries.region.asia', 'Asia')}>
                          <option value="Japón">{t('countries.japan', 'Japón')}</option>
                          <option value="China">{t('countries.china', 'China')}</option>
                          <option value="Corea del Sur">{t('countries.south_korea', 'Corea del Sur')}</option>
                          <option value="Singapur">{t('countries.singapore', 'Singapur')}</option>
                          <option value="Hong Kong">{t('countries.hong_kong', 'Hong Kong')}</option>
                          <option value="Taiwán">{t('countries.taiwan', 'Taiwán')}</option>
                          <option value="Tailandia">{t('countries.thailand', 'Tailandia')}</option>
                          <option value="Malasia">{t('countries.malaysia', 'Malasia')}</option>
                          <option value="Indonesia">{t('countries.indonesia', 'Indonesia')}</option>
                          <option value="Filipinas">{t('countries.philippines', 'Filipinas')}</option>
                          <option value="Vietnam">{t('countries.vietnam', 'Vietnam')}</option>
                          <option value="India">{t('countries.india', 'India')}</option>
                        </optgroup>
                        <optgroup label={t('countries.region.oceania', 'Oceanía')}>
                          <option value="Australia">{t('countries.australia', 'Australia')}</option>
                          <option value="Nueva Zelanda">{t('countries.new_zealand', 'Nueva Zelanda')}</option>
                        </optgroup>
                        <optgroup label={t('countries.region.america', 'América')}>
                          <option value="Estados Unidos">{t('countries.united_states', 'Estados Unidos')}</option>
                          <option value="Canadá">{t('countries.canada', 'Canadá')}</option>
                          <option value="México">{t('countries.mexico', 'México')}</option>
                          <option value="Brasil">{t('countries.brazil', 'Brasil')}</option>
                          <option value="Argentina">{t('countries.argentina', 'Argentina')}</option>
                          <option value="Chile">{t('countries.chile', 'Chile')}</option>
                          <option value="Colombia">{t('countries.colombia', 'Colombia')}</option>
                          <option value="Perú">{t('countries.peru', 'Perú')}</option>
                          <option value="Ecuador">{t('countries.ecuador', 'Ecuador')}</option>
                          <option value="Venezuela">{t('countries.venezuela', 'Venezuela')}</option>
                          <option value="Uruguay">{t('countries.uruguay', 'Uruguay')}</option>
                          <option value="Panamá">{t('countries.panama', 'Panamá')}</option>
                          <option value="Costa Rica">{t('countries.costa_rica', 'Costa Rica')}</option>
                          <option value="República Dominicana">{t('countries.dominican_republic', 'República Dominicana')}</option>
                          <option value="Puerto Rico">{t('countries.puerto_rico', 'Puerto Rico')}</option>
                        </optgroup>
                        <optgroup label={t('countries.region.other', 'Otros')}>
                          <option value="Otro">{t('countries.other', 'Otro')}</option>
                        </optgroup>
                      </select>
                    </div>
                  </motion.div>
                )}

                {/* Paso 3: Contacto */}
                {currentStep === 3 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-4"
                    data-testid="step-3-contact"
                  >
                    <h3 className="text-xl font-bold mb-4" style={{color: '#0d0d0d'}}>
                      <T keyName="auth.register.contact_info">Información de Contacto</T>
                    </h3>

                    <div>
                      <label htmlFor="telefono" className="block text-sm font-medium mb-2" style={{color: '#0d0d0d'}}>
                        <T keyName="auth.register.phone">Teléfono *</T>
                      </label>
                      <input
                        id="telefono"
                        name="telefono"
                        type="tel"
                        value={formData.telefono}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border-2 rounded-2xl focus:outline-none transition-colors ${
                          formErrors.telefono
                            ? 'border-red-300 focus:border-red-500'
                            : 'border-gray-200 focus:border-gray-400'
                        }`}
                        style={{color: '#0d0d0d'}}
                        placeholder="Teléfono fijo"
                        data-testid="phone-input"
                      />
                      {formErrors.telefono && (
                        <p className="mt-2 text-sm text-red-600" data-testid="phone-error">{formErrors.telefono}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="fax" className="block text-sm font-medium mb-2" style={{color: '#0d0d0d'}}>
                          <T keyName="auth.register.fax">FAX</T>
                        </label>
                        <input
                          id="fax"
                          name="fax"
                          type="tel"
                          value={formData.fax}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-gray-400 transition-colors"
                          style={{color: '#0d0d0d'}}
                          placeholder="FAX"
                          data-testid="fax-input"
                        />
                      </div>

                      <div>
                        <label htmlFor="movil" className="block text-sm font-medium mb-2" style={{color: '#0d0d0d'}}>
                          <T keyName="auth.register.mobile">Móvil</T>
                        </label>
                        <input
                          id="movil"
                          name="movil"
                          type="tel"
                          value={formData.movil}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-gray-400 transition-colors"
                          style={{color: '#0d0d0d'}}
                          placeholder="Móvil"
                          data-testid="mobile-input"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="personaContacto" className="block text-sm font-medium mb-2" style={{color: '#0d0d0d'}}>
                        <T keyName="auth.register.contact_person">Persona de contacto</T>
                      </label>
                      <input
                        id="personaContacto"
                        name="personaContacto"
                        type="text"
                        value={formData.personaContacto}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-gray-400 transition-colors"
                        style={{color: '#0d0d0d'}}
                        placeholder="Nombre de la persona de contacto"
                        data-testid="contact-person-input"
                      />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2" style={{color: '#0d0d0d'}}>
                        <T keyName="auth.register.email">Email *</T>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border-2 rounded-2xl focus:outline-none transition-colors ${
                        formErrors.email
                          ? 'border-red-300 focus:border-red-500'
                          : 'border-gray-200 focus:border-gray-400'
                      }`}
                      style={{color: '#0d0d0d'}}
                      placeholder="tu@email.com"
                      data-testid="register-email-input"
                    />
                    {formErrors.email && (
                      <p className="mt-2 text-sm text-red-600" data-testid="register-email-error">{formErrors.email}</p>
                    )}
                  </div>

                    <div>
                      <label htmlFor="web" className="block text-sm font-medium mb-2" style={{color: '#0d0d0d'}}>
                        <T keyName="auth.register.web">Web</T>
                      </label>
                      <input
                        id="web"
                        name="web"
                        type="url"
                        value={formData.web}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-gray-400 transition-colors"
                        style={{color: '#0d0d0d'}}
                        placeholder="https://www.tuempresa.com"
                        data-testid="web-input"
                      />
                    </div>

                    <div>
                      <label htmlFor="comentario" className="block text-sm font-medium mb-2" style={{color: '#0d0d0d'}}>
                        <T keyName="auth.register.comment">Comentario</T>
                      </label>
                      <textarea
                        id="comentario"
                        name="comentario"
                        value={formData.comentario}
                        onChange={handleChange}
                        rows={3}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-gray-400 transition-colors resize-none"
                        style={{color: '#0d0d0d'}}
                        placeholder="Información adicional sobre tu empresa..."
                        data-testid="comment-input"
                      />
                    </div>
                  </motion.div>
                )}

                {/* Paso 4: Acceso */}
                {currentStep === 4 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-4"
                    data-testid="step-4-access"
                  >
                    <h3 className="text-xl font-bold mb-4" style={{color: '#0d0d0d'}}>
                      <T keyName="auth.register.create_access">Crear Acceso</T>
                    </h3>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium mb-2" style={{color: '#0d0d0d'}}>
                        <T keyName="auth.register.password">Contraseña *</T>
                    </label>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border-2 rounded-2xl focus:outline-none transition-colors ${
                        formErrors.password
                          ? 'border-red-300 focus:border-red-500'
                          : 'border-gray-200 focus:border-gray-400'
                      }`}
                      style={{color: '#0d0d0d'}}
                      placeholder="••••••••"
                      data-testid="register-password-input"
                    />
                    {formErrors.password && (
                      <p className="mt-2 text-sm text-red-600" data-testid="register-password-error">{formErrors.password}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium mb-2" style={{color: '#0d0d0d'}}>
                        <T keyName="auth.register.confirm_password">Confirmar contraseña *</T>
                    </label>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border-2 rounded-2xl focus:outline-none transition-colors ${
                        formErrors.confirmPassword
                          ? 'border-red-300 focus:border-red-500'
                          : 'border-gray-200 focus:border-gray-400'
                      }`}
                      style={{color: '#0d0d0d'}}
                      placeholder="••••••••"
                      data-testid="confirm-password-input"
                    />
                    {formErrors.confirmPassword && (
                      <p className="mt-2 text-sm text-red-600" data-testid="confirm-password-error">{formErrors.confirmPassword}</p>
                    )}
                    </div>
                  </motion.div>
                )}

                {/* Error de envío */}
                {submitError && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                    {submitError}
                  </div>
                )}

                {/* Botones de navegación */}
                <div className="flex justify-between pt-6">
                  {currentStep > 1 && (
                    <motion.button
                      type="button"
                      onClick={prevStep}
                      disabled={isSubmitting}
                      className="px-6 py-3 rounded-full font-medium transition-all border-2 disabled:opacity-50"
                      style={{
                        backgroundColor: 'transparent',
                        color: '#0d0d0d',
                        borderColor: '#0d0d0d'
                      }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      data-testid="previous-step-button"
                    >
                      <T keyName="auth.register.previous">Anterior</T>
                    </motion.button>
                )}

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                    className="px-6 py-3 rounded-full font-medium transition-all border-2 shadow-md hover:shadow-lg disabled:opacity-50"
                  style={{
                    backgroundColor: '#f6cf02',
                    color: '#0d0d0d',
                    borderColor: '#0d0d0d'
                  }}
                  whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                  whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                  data-testid={currentStep === totalSteps ? "submit-button" : "next-step-button"}
                >
                  {isSubmitting ? (
                                            <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-t-2 mr-3" style={{borderColor: '#0d0d0d'}}></div>
                            <T keyName="auth.register.sending">Enviando...</T>
                        </div>
                        ) : currentStep === totalSteps ? (
                          <T keyName="auth.register.send_request">Enviar Solicitud</T>
                      ) : (
                          <T keyName="auth.register.next">Siguiente</T>
                      )}
                </motion.button>
                </div>

                <div className="text-center">
                  <p className="text-gray-600">
                    <T keyName="auth.register.already_have_access">¿Ya tienes acceso?</T>{' '}
                    <Link to="/login" className="font-medium hover:underline" style={{color: '#0d0d0d'}} data-testid="login-link">
                      <T keyName="auth.register.login_here">Inicia sesión aquí</T>
                    </Link>
                  </p>
                </div>
              </form>
            </div>
          </motion.div>

          {/* Imagen y contenido - Lado derecho */}
          <motion.div 
            className="hidden lg:flex items-center justify-center bg-white relative overflow-hidden"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="relative z-10 max-w-lg px-8 text-center">
              
              <h2 className="text-3xl font-bold mb-4" style={{color: '#0d0d0d'}}>
                <T keyName="auth.register.request_title">Solicitud de Acceso para Distribuidores</T>
              </h2>
              
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                <T keyName="auth.register.request_description">Esta área está diseñada especialmente para distribuidores mayoristas y tiendas especializadas interesados en comercializar los productos de Jarad Pet Food.</T>
              </p>
              
              <div className="space-y-4 mb-6">
                <div className="bg-gray-50 rounded-xl p-4 text-left">
                  <h3 className="font-bold mb-2" style={{color: '#0d0d0d'}}><T keyName="auth.register.how_to_access">¿Cómo acceder a la plataforma?</T></h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    <T keyName="auth.register.request_description_2">Solicita el acceso rellenando este formulario. Una vez verificada tu solicitud, te proporcionaremos las credenciales necesarias para disfrutar de todos los beneficios de este espacio exclusivo.</T>
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="w-8 h-8 rounded-full mx-auto mb-2" style={{backgroundColor: '#f6cf02'}}></div>
                  <p className="font-medium" style={{color: '#0d0d0d'}}><T keyName="auth.register.wholesale_prices">Precios Mayoristas</T></p>
                  <p className="text-gray-500 text-xs"><T keyName="auth.register.special_conditions">Condiciones especiales</T></p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="w-8 h-8 rounded-full mx-auto mb-2" style={{backgroundColor: '#f6cf02'}}></div>
                  <p className="font-medium" style={{color: '#0d0d0d'}}><T keyName="auth.register.technical_advice">Asesoramiento Técnico</T></p>
                  <p className="text-gray-500 text-xs"><T keyName="auth.register.specialized_support">Soporte especializado</T></p>
                </div>
              </div>
            </div>
            
            {/* Elementos decorativos */}
            <motion.div 
              className="absolute top-10 right-10 w-20 h-20 rounded-full opacity-20"
              style={{backgroundColor: '#f6cf02'}}
              animate={{ 
                scale: [1, 1.2, 1],
                opacity: [0.2, 0.3, 0.2]
              }}
              transition={{ 
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <motion.div 
              className="absolute bottom-10 left-10 w-16 h-16 rounded-full opacity-15"
              style={{backgroundColor: '#f6cf02'}}
              animate={{ 
                scale: [1, 1.3, 1],
                opacity: [0.15, 0.25, 0.15]
              }}
              transition={{ 
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1
              }}
            />
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default RegisterPage; 